import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import BackButton from 'common/BackButton/BackButton';
import ErrorState from 'common/ErrorState/ErrorState';
import Loader from 'components/Loader/Loader';
import DancerCard from '../../DancerCard/DancerCard';
import CoachCard from '../../CoachCard/CoachCard';
import Facebook from 'materials/icons/Facebook';
import EmailIcon from 'materials/icons/Email';
import Insta from 'materials/icons/Insta';

import EphanDataService, { ARCHIVE_STATE } from 'services/EphanDataService';
import { contactLinks } from 'helpers/clubContacts';
import { addressCity } from 'helpers/clubLocation';
import { useLanguage } from 'hooks/useLanguage';
import './ClubInfo.css';

/**
 * Сторінка клубу — ADR-0005, ADR-0015, ADR-0016.
 *
 * Один запит `/clubs/{id}` дає все, крім танцюристів: засновання, контакти,
 * усі адреси (головну й філії) та тренерський склад. Танцюристів джерело
 * навмисно не вкладає — їх бувають сотні, — тому вони окремим списком за
 * фільтром `clubId`.
 *
 * Архів не заважає: сторінка клубу не питає розділу реєстру, тож посилання на
 * закритий клуб працює, а сам стан показано написом.
 */
export default function ClubInfo() {
  const language = useLanguage();
  const { clubId } = useParams();

  const [club, setClub] = useState(null);
  const [state, setState] = useState({ status: 'loading', errors: [] });
  const [dancers, setDancers] = useState([]);
  const [activeTab, setActiveTab] = useState('dancers');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setState({ status: 'loading', errors: [] });

    async function load() {
      const { result, notFound, errors } = await EphanDataService.getClub(clubId);

      if (cancelled) return;

      if (notFound) {
        setState({ status: 'notFound', errors: [] });
        return;
      }

      if (errors.length > 0) {
        setState({ status: 'failed', errors });
        return;
      }

      setClub(result);
      setState({ status: 'succeeded', errors: [] });

      // Склад архівного клубу шукається в архіві: разом із клубом туди
      // потрапили і його записи.
      const registryState = result.registryState === ARCHIVE_STATE ? ARCHIVE_STATE : undefined;
      const roster = await EphanDataService.getDancers({ clubId, limit: 100, registryState });

      if (!cancelled) setDancers(roster.result);
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [clubId, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  const rawName = (club?.name || '').trim();
  const title = rawName.includes('(') ? rawName.split('(')[0].trim() : rawName;
  const locations = club?.locations?.length ? club.locations : club?.address ? [{ type: 'main', address: club.address }] : [];
  const mainLocation = locations.find((location) => location.type === 'main') || locations[0] || null;
  const city = mainLocation?.city || addressCity(mainLocation?.address);
  const branches = locations.filter((location) => location !== mainLocation);
  const isArchived = club?.registryState === ARCHIVE_STATE;

  const contacts = contactLinks(club?.contacts);
  const website = contacts.find(({ key }) => key === 'website');
  const phone = contacts.find(({ key }) => key === 'phone');
  const buttons = contacts.filter(({ key }) => key === 'facebook' || key === 'instagram' || key === 'email');

  const coaches = club?.coaches || [];

  const tabs = [
    {
      id: 'dancers',
      label: language === 'en' ? 'Dancers' : 'Танцюристи',
      count: dancers.length || club?.dancersCount || 0,
    },
    {
      id: 'coaches',
      label: language === 'en' ? 'Coaches' : 'Тренери',
      count: coaches.length || club?.coachesCount || 0,
    },
  ];

  const icons = { facebook: <Facebook />, instagram: <Insta />, email: <EmailIcon /> };

  return (
    <section className='club-info section'>
      <div className='container'>
        <BackButton />

        {state.status === 'failed' && <ErrorState messages={state.errors} onRetry={retry} />}

        {state.status === 'notFound' && <ErrorState variant='page' error={{ kind: 'notFound', status: 404 }} />}

        {state.status === 'loading' && <Loader />}

        {state.status === 'succeeded' && club && (
          <>
            <div className='entity-detail'>
              {club.logo ? (
                <img className='entity-avatar entity-avatar--contain' src={club.logo} alt='' />
              ) : (
                <span className='entity-avatar entity-avatar--contain club-logo-placeholder' aria-hidden='true' />
              )}

              <div className='entity-body'>
                <h1 className='entity-name'>{title}</h1>

                {isArchived && (
                  <p className='club-archived-note'>
                    {language === 'en' ? 'This club is in the archive of the registry.' : 'Цей клуб — в архіві реєстру.'}
                  </p>
                )}

                <dl className='entity-facts'>
                  <div>
                    <dt>{language === 'en' ? 'Coaches' : 'Тренерів'}</dt>
                    <dd className='tnum'>{club.coachesCount ?? '-'}</dd>
                  </div>
                  <div>
                    <dt>{language === 'en' ? 'Dancers' : 'Танцюристів'}</dt>
                    <dd className='tnum'>{club.dancersCount ?? '-'}</dd>
                  </div>
                  <div>
                    <dt>{language === 'en' ? 'Town' : 'Місто'}</dt>
                    <dd>{city || (language === 'en' ? 'Not specified' : 'Не вказано')}</dd>
                  </div>
                  {/* Джерело віддає рік або повну дату — що записали, те й показуємо. */}
                  {club.foundedOn && (
                    <div>
                      <dt>{language === 'en' ? 'Founded' : 'Засновано'}</dt>
                      <dd className='tnum'>{club.foundedOn}</dd>
                    </div>
                  )}
                  {phone && (
                    <div>
                      <dt>{language === 'en' ? 'Phone' : 'Телефон'}</dt>
                      <dd>
                        <a className='linked' href={phone.href}>
                          {phone.label}
                        </a>
                      </dd>
                    </div>
                  )}
                  {website && (
                    <div>
                      <dt>{language === 'en' ? 'Website' : 'Сайт'}</dt>
                      <dd>
                        <a className='linked' href={website.href} target='_blank' rel='noreferrer'>
                          {website.label}
                        </a>
                      </dd>
                    </div>
                  )}
                </dl>

                {locations.length > 0 && (
                  <div className='club-locations'>
                    <h2 className='club-locations-title'>
                      {branches.length > 0
                        ? language === 'en'
                          ? 'Addresses'
                          : 'Адреси'
                        : language === 'en'
                        ? 'Address'
                        : 'Адреса'}
                    </h2>
                    <ul>
                      {locations.map((location) => (
                        <li className='club-location' key={`${location.type}-${location.address}`}>
                          <span className='club-location-address'>{location.address}</span>
                          {location !== mainLocation && (
                            <span className='club-location-tag'>{language === 'en' ? 'branch' : 'філія'}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {buttons.length > 0 && (
                  <div className='detail-socials-wrapper'>
                    {buttons.map(({ key, href, label }) => (
                      <a
                        key={key}
                        href={href}
                        target={key === 'email' ? undefined : '_blank'}
                        rel='noreferrer'
                        className={`social-btn ${key}`}
                        aria-label={label}
                      >
                        {icons[key]}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className='entity-section'>
              <div className='event-detail-buttons' role='tablist'>
                {tabs.map((tab) => (
                  <button
                    type='button'
                    role='tab'
                    key={tab.id}
                    id={`club-tab-${tab.id}`}
                    aria-selected={activeTab === tab.id}
                    aria-controls={`club-panel-${tab.id}`}
                    className={`event-details-list ${activeTab === tab.id ? 'is-active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                    <span className='tab-count tnum'>{tab.count}</span>
                  </button>
                ))}
              </div>

              <div
                className='club-catalog'
                role='tabpanel'
                id={`club-panel-${activeTab}`}
                aria-labelledby={`club-tab-${activeTab}`}
              >
                {activeTab === 'dancers' &&
                  (dancers.length > 0 ? (
                    <div className='catalog-wrapper'>
                      {dancers.map((dancer) => (
                        <DancerCard dancer={dancer} key={dancer.id} />
                      ))}
                    </div>
                  ) : (
                    <p className='empty-note'>{language === 'en' ? 'No dancers' : 'Танцюристів немає'}</p>
                  ))}

                {activeTab === 'coaches' &&
                  (coaches.length > 0 ? (
                    <div className='catalog-wrapper'>
                      {coaches.map((coach) => (
                        <CoachCard coach={coach} key={coach.id} />
                      ))}
                    </div>
                  ) : (
                    <p className='empty-note'>{language === 'en' ? 'No coaches' : 'Тренерів немає'}</p>
                  ))}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
